import { z } from 'zod';

import { apiClient } from '@/lib/api-client';
import { authoredCommentPageSchema, type AuthoredCommentPage } from '@/lib/schemas/comment';
import { postPageSchema, type PostPage } from '@/lib/schemas/post';
import {
  followResultSchema,
  followingListSchema,
  publicProfileSchema,
  type FollowingEntry,
  type PublicProfile,
} from '@/lib/schemas/profile';
import { privateUserSchema, type PrivateUser } from '@/lib/schemas/user';

export interface UpdateProfileInput {
  displayName?: string;
  bio?: string;
  username?: string;
  homeTown?: string;
  homeCountryCode?: string;
  addressLine1?: string;
  addressLine2?: string;
  town?: string;
  region?: string;
  postalCode?: string;
  countryCode?: string;
  /** Spec §8.8: the weekly digest opt-in. */
  digestOptIn?: boolean;
}

export const profileApi = {
  get: (username: string, signal?: AbortSignal): Promise<PublicProfile> =>
    apiClient.get(`/users/${username}`, publicProfileSchema, signal ? { signal } : {}),

  posts: (
    username: string,
    query: Record<string, string>,
    signal?: AbortSignal,
  ): Promise<PostPage> =>
    apiClient.get(`/users/${username}/posts`, postPageSchema, {
      query,
      ...(signal ? { signal } : {}),
    }),

  comments: (
    username: string,
    query: Record<string, string>,
    signal?: AbortSignal,
  ): Promise<AuthoredCommentPage> =>
    apiClient.get(`/users/${username}/comments`, authoredCommentPageSchema, {
      query,
      ...(signal ? { signal } : {}),
    }),

  update: (body: UpdateProfileInput): Promise<PrivateUser> =>
    apiClient.patch('/users/me', privateUserSchema, { body }),

  setAvatar: (mediaId: string | null): Promise<PrivateUser> =>
    apiClient.post('/users/me/avatar', privateUserSchema, { body: { mediaId } }),

  follow: (userId: string) => apiClient.post(`/users/${userId}/follow`, followResultSchema),
  unfollow: (userId: string) => apiClient.delete(`/users/${userId}/follow`, followResultSchema),

  followCountry: (code: string) => apiClient.post(`/follows/country/${code}`, followResultSchema),
  unfollowCountry: (code: string) =>
    apiClient.delete(`/follows/country/${code}`, followResultSchema),

  followTown: (countryCode: string, town: string) =>
    apiClient.post('/follows/town', followResultSchema, { body: { countryCode, town } }),
  unfollowTown: (countryCode: string, town: string) =>
    apiClient.delete('/follows/town', followResultSchema, { body: { countryCode, town } }),

  following: (signal?: AbortSignal): Promise<FollowingEntry[]> =>
    apiClient.get('/users/me/following', followingListSchema, signal ? { signal } : {}),

  deleteAccount: (): Promise<{ purgeAfter: string }> =>
    apiClient.delete('/users/me', z.object({ purgeAfter: z.string() })),
};
